import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { Icon } from '../Icon/Icon';
import styles from './InfoTip.module.scss';

export interface InfoTipProps {
  /** Подпись кнопки для экранного диктора. */
  label: string;
  /** Первая строка подсказки. */
  heading?: string;
  lines: readonly string[];
}

/** Отступ подсказки от края окна, px: подсказка не выходит за экран и не даёт горизонтальной прокрутки. */
const EDGE = 8;

/**
 * Кружок «i» после подписи: нажатие показывает подсказку под ним, повторное нажатие, Esc и щелчок
 * мимо — прячут.
 */
export function InfoTip({ label, heading, lines }: InfoTipProps): ReactElement {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Сдвиг считается по месту подсказки после показа: у правого края она уходит влево.
  useLayoutEffect(() => {
    const element = tip.current;
    if (!open || !element) return;
    element.style.setProperty('--info-tip-shift', '0px');
    const rect = element.getBoundingClientRect();
    const width = document.documentElement.clientWidth;
    const shift = rect.right > width - EDGE ? width - EDGE - rect.right : rect.left < EDGE ? EDGE - rect.left : 0;
    element.style.setProperty('--info-tip-shift', `${Math.round(shift)}px`);
  }, [open]);

  return (
    <span ref={root} className={styles.root}>
      <button
        type="button"
        className={styles.button}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="info" size="1.15em" />
      </button>
      {open ? (
        <span ref={tip} id={id} role="tooltip" className={styles.tip}>
          {heading ? <span className={styles.heading}>{heading}</span> : null}
          {lines.map((line) => (
            <span key={line} className={styles.line}>
              {line}
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
}
